import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import {
  tournamentDayApi,
  type TournamentOperatorScheduleParams,
} from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import {
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useSearchParams } from "react-router-dom";

import { listCourts, listDraws, listEvents } from "@/libs/tournamentApi";

type Props = {
  actions?: ReactNode;
  focusSearchRef?: RefObject<HTMLInputElement | null>;
  onOpenBoard?: () => void;
  onSelectMatch?: (match: Schema["TournamentMatchRef"]) => void;
  tournamentId: string;
  revision: number;
  selectedMatch?: Schema["TournamentMatchRef"] | null;
};

type FilterOptions = {
  courts: Array<{ court_id: string; name: string }>;
  draws: Array<{ draw_id: string; name: string }>;
  events: Array<{ event_id: string; name: string }>;
};

const contextKeys = ["day", "event_id", "draw_id", "court_id", "query"];

function label(value: string): string {
  return value.replaceAll("_", " ");
}

function displayDay(value: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
      weekday: "short",
      year: "numeric",
    }).format(new Date(`${value}T12:00:00Z`));
  } catch {
    return value;
  }
}

function displayTime(value: string, venueTimezone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      month: "short",
      timeZone: venueTimezone,
      weekday: "short",
    }).format(new Date(value));
  } catch {
    return "Time unavailable";
  }
}

function selectedOption<T extends { [key: string]: string }>(
  items: T[],
  idKey: keyof T,
  labelKey: keyof T,
  selected: string | undefined,
  fallbackLabel: string,
): T[] {
  if (!selected || items.some((item) => item[idKey] === selected)) return items;
  return [{ [idKey]: selected, [labelKey]: fallbackLabel } as T, ...items];
}

export function OperatorOrderOfPlay({
  actions,
  focusSearchRef,
  onOpenBoard,
  onSelectMatch,
  revision,
  selectedMatch,
  tournamentId,
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo<TournamentOperatorScheduleParams>(
    () => ({
      courtId: searchParams.get("court_id") || undefined,
      day: searchParams.get("day") || undefined,
      drawId: searchParams.get("draw_id") || undefined,
      eventId: searchParams.get("event_id") || undefined,
      query: searchParams.get("query") || undefined,
    }),
    [searchParams],
  );
  const filterKey = JSON.stringify(filters);
  const paginationKey = `${tournamentId}:${revision}:${filterKey}`;
  const [pagination, setPagination] = useState({
    context: paginationKey,
    cursors: [] as string[],
  });
  const cursorStack =
    pagination.context === paginationKey ? pagination.cursors : [];
  const cursor = cursorStack.at(-1);
  useEffect(() => {
    setPagination((current) =>
      current.context === paginationKey
        ? current
        : { context: paginationKey, cursors: [] },
    );
  }, [paginationKey]);

  const resetPagination = () => {
    setPagination({ context: paginationKey, cursors: [] });
  };

  const schedule = useQuery({
    queryKey: [
      "tournament-operator-schedule",
      tournamentId,
      revision,
      filters,
      cursor,
    ],
    queryFn: () =>
      tournamentDayApi.getTournamentOperatorSchedule(tournamentId, {
        ...filters,
        cursor,
        limit: 100,
      }),
    enabled: Boolean(tournamentId),
    retry: false,
  });
  const options = useQuery<FilterOptions>({
    queryKey: ["tournament-operator-schedule-filter-options", tournamentId],
    queryFn: async () => {
      const [eventPage, draws, courts] = await Promise.all([
        listEvents(tournamentId, 1, 100),
        listDraws(tournamentId),
        listCourts(tournamentId),
      ]);
      return { courts, draws, events: eventPage.items };
    },
    enabled: Boolean(tournamentId),
    retry: false,
  });

  const updateContext = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    resetPagination();
    setSearchParams(next, { replace: true });
  };
  const resetContext = () => {
    const next = new URLSearchParams(searchParams);
    contextKeys.forEach((key) => next.delete(key));
    resetPagination();
    setSearchParams(next, { replace: true });
  };
  const page = schedule.data;
  // The date options use primitives rather than entity-shaped API values.
  const pageDays = page?.available_days ?? [];
  const availableDays =
    filters.day && !pageDays.includes(filters.day)
      ? [filters.day, ...pageDays]
      : pageDays;
  const events = selectedOption(
    options.data?.events ?? [],
    "event_id",
    "name",
    filters.eventId ?? undefined,
    "Selected event",
  );
  const draws = selectedOption(
    options.data?.draws ?? [],
    "draw_id",
    "name",
    filters.drawId ?? undefined,
    "Selected draw",
  );
  const courts = selectedOption(
    options.data?.courts ?? [],
    "court_id",
    "name",
    filters.courtId ?? undefined,
    "Selected court",
  );

  return (
    <Paper
      component="section"
      aria-label="Order of play"
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ alignItems: { md: "center" }, justifyContent: "space-between" }}
        >
          <Typography component="h2" variant="h6">
            Order of play
          </Typography>
          {page ? (
            <Typography color="text.secondary" variant="body2">
              {page.total} scheduled {page.total === 1 ? "match" : "matches"}
            </Typography>
          ) : null}
        </Stack>

        <Box
          component="div"
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(220px, 2fr) repeat(4, minmax(130px, 1fr)) auto",
            },
          }}
        >
          <TextField
            inputRef={focusSearchRef}
            label="Search order of play"
            onChange={(event) => updateContext("query", event.target.value)}
            size="small"
            type="search"
            value={filters.query ?? ""}
          />
          <FormControl size="small">
            <InputLabel id="order-of-play-day-label">Day</InputLabel>
            <Select
              label="Day"
              labelId="order-of-play-day-label"
              onChange={(event) => updateContext("day", event.target.value)}
              value={filters.day ?? ""}
            >
              <MenuItem value="">All days</MenuItem>
              {availableDays.map((day) => (
                <MenuItem key={day} value={day}>
                  {displayDay(day)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="order-of-play-event-label">Event</InputLabel>
            <Select
              label="Event"
              labelId="order-of-play-event-label"
              onChange={(event) =>
                updateContext("event_id", event.target.value)
              }
              value={filters.eventId ?? ""}
            >
              <MenuItem value="">All events</MenuItem>
              {events.map((event) => (
                <MenuItem key={event.event_id} value={event.event_id}>
                  {event.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="order-of-play-draw-label">Draw</InputLabel>
            <Select
              label="Draw"
              labelId="order-of-play-draw-label"
              onChange={(event) => updateContext("draw_id", event.target.value)}
              value={filters.drawId ?? ""}
            >
              <MenuItem value="">All draws</MenuItem>
              {draws.map((draw) => (
                <MenuItem key={draw.draw_id} value={draw.draw_id}>
                  {draw.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="order-of-play-court-label">Court</InputLabel>
            <Select
              label="Court"
              labelId="order-of-play-court-label"
              onChange={(event) =>
                updateContext("court_id", event.target.value)
              }
              value={filters.courtId ?? ""}
            >
              <MenuItem value="">All courts</MenuItem>
              {courts.map((court) => (
                <MenuItem key={court.court_id} value={court.court_id}>
                  {court.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button onClick={resetContext} size="small" variant="text">
            Clear filters
          </Button>
          {onOpenBoard ? (
            <Button onClick={onOpenBoard} size="small" variant="outlined">
              Court board
            </Button>
          ) : null}
          {actions}
        </Box>

        {schedule.isPending ? (
          <Typography aria-live="polite">Loading order of play…</Typography>
        ) : null}
        {schedule.isError ? (
          <Alert
            action={
              <Button
                onClick={() => {
                  const hasPageCursor = cursorStack.length > 0;
                  resetPagination();
                  if (!hasPageCursor) void schedule.refetch();
                }}
                size="small"
              >
                Load current schedule
              </Button>
            }
            role="alert"
            severity="error"
          >
            Order of play is unavailable. Load the current schedule.
          </Alert>
        ) : null}
        {page && !schedule.isError ? (
          <>
            <TableContainer
              sx={{ border: 1, borderColor: "divider", maxHeight: 520 }}
            >
              <Table aria-label="Order of play" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Scheduled</TableCell>
                    <TableCell>Court</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Draw</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Inspect</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {page.items.map((item) => (
                    <TableRow
                      key={`${item.match.kind}:${item.match.match_id}`}
                      selected={
                        selectedMatch?.kind === item.match.kind &&
                        selectedMatch.match_id === item.match.match_id
                      }
                    >
                      <TableCell>
                        {displayTime(item.start_at, item.venue_timezone)}
                      </TableCell>
                      <TableCell>{item.court_label}</TableCell>
                      <TableCell>{item.event_label}</TableCell>
                      <TableCell>{item.draw_label}</TableCell>
                      <TableCell>{item.match_display_label}</TableCell>
                      <TableCell>
                        {label(item.execution_state)} ·{" "}
                        {label(item.dispatch_state)}
                        {item.pinned ? " · pinned" : ""}
                      </TableCell>
                      <TableCell>
                        <Button
                          aria-label={`Inspect ${item.match_display_label}`}
                          onClick={() => onSelectMatch?.(item.match)}
                          size="small"
                        >
                          Inspect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {page.items.length === 0 ? (
              <Typography color="text.secondary">
                No published matches match this view.
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1}>
              <Button
                disabled={cursorStack.length === 0 || schedule.isFetching}
                onClick={() =>
                  setPagination({
                    context: paginationKey,
                    cursors: cursorStack.slice(0, -1),
                  })
                }
                size="small"
              >
                Previous page
              </Button>
              <Button
                disabled={!page.next_cursor || schedule.isFetching}
                onClick={() => {
                  if (!page.next_cursor) return;
                  setPagination({
                    context: paginationKey,
                    cursors: [...cursorStack, page.next_cursor],
                  });
                }}
                size="small"
              >
                Next page
              </Button>
            </Stack>
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
